import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  deDE,
} from '@mui/x-data-grid';
import React, { useState } from 'react';
import { IUser } from '../Interfaces/IUser';
import { Alert, Box, IconButton, Typography } from '@mui/material';
import { Add, Check, Clear } from '@mui/icons-material';
import { ShowWasSaved } from '../core/ShowWasSaved';
import { FullScreenDialog } from '../core/FullScreenDialog';
import { UserFullEdit } from './UserFullEdit';
import type { IUserGroup } from '../Interfaces/IUserGroup';
import type { IRole } from '../Interfaces/IRole';
import type { IDayOffWorkCategory } from '../Interfaces/IDayOffWorkCategory';
import { LoadingSpinner } from '../core/LoadingSpinner';
import { useGetDaysOffWorkCategories } from '../hooks/reactQuerys/DaysOffWorkQueries';
import { useGetRoles } from '../hooks/reactQuerys/RoleQueries';
import {
  useGetUserGroups,
  useGetUsers,
} from '../hooks/reactQuerys/UserQueries';

const columns: GridColDef[] = [
  { field: 'idUser', headerName: 'Nr.', width: 90 },
  {
    field: 'isBlocked',
    headerName: 'Aktiv',
    width: 90,
    renderCell: (params: GridRenderCellParams) =>
      Boolean(params.row.isBlocked) ? (
        <Clear color='error' />
      ) : (
        <Check color='success' />
      ),
  },
  {
    field: 'isNotary',
    headerName: 'Notar',
    width: 90,
    renderCell: (params: GridRenderCellParams) =>
      Boolean(params.row.isNotary) ? <Check color='success' /> : <Clear />,
  },
  { field: 'Role', headerName: 'Rolle', width: 150 },
  { field: 'Login', headerName: 'Benutzername', width: 150 },
  { field: 'FirstName', headerName: 'Vorname', width: 150 },
  { field: 'LastName', headerName: 'Nachname', flex: 1 },
];

type IProps = {
  initialUsers: IUser[];
  roles: IRole[];
  userGroups: IUserGroup[];
  daysOffWorkCategories: IDayOffWorkCategory[];
};

export const UserOverview = () => {
  const { daysOffWorkCategories, isLoadingDaysOffWorkCategories } =
    useGetDaysOffWorkCategories();
  const { userGroups, isLoadingUserGroups } = useGetUserGroups();
  const { users, isLoadingUsers } = useGetUsers();
  const { roles, isLoadingRoles } = useGetRoles();

  if (
    isLoadingDaysOffWorkCategories ||
    isLoadingUserGroups ||
    isLoadingUsers ||
    isLoadingRoles
  ) {
    return <LoadingSpinner />;
  }

  if (!daysOffWorkCategories || !userGroups || !users || !roles) {
    return <Alert severity='error'>Fehler!</Alert>;
  }

  return (
    <Users
      initialUsers={users}
      roles={roles}
      userGroups={userGroups}
      daysOffWorkCategories={daysOffWorkCategories}
    />
  );
};

export const Users = ({
  initialUsers,
  roles,
  userGroups,
  daysOffWorkCategories,
}: IProps) => {
  const [users, setUsers] = useState<IUser[]>(initialUsers);
  const [idUser, setIdUser] = useState<number | null>(null);
  const [wasSaved, setWasSaved] = useState(false);
  const [wasSavedSuccesfully, setWasSavedSuccesfully] = useState(false);

  return (
    <>
      <ShowWasSaved
        wasSaved={wasSaved}
        setWasSaved={setWasSaved}
        wasSuccessfullySaved={wasSavedSuccesfully}
      />
      <FullScreenDialog
        title='Benutzer'
        isOpen={idUser !== null}
        onClose={() => setIdUser(null)}
      >
        <>
          {idUser !== null && (
            <UserFullEdit
              key={`idUSer-${idUser}`}
              idUser={idUser}
              setIdUser={setIdUser}
              userArray={users}
              userGroupArray={userGroups}
              setUserArray={setUsers}
              roleArray={roles}
              setWasSaved={setWasSaved}
              setWasSavedSuccesfully={setWasSavedSuccesfully}
              dayOffWorkCategoryArray={daysOffWorkCategories}
            />
          )}
        </>
      </FullScreenDialog>
      <Box sx={{ m: 2, mt: 0 }}>
        <Typography variant='h4'>
          Benutzer
          <IconButton sx={{ float: 'right' }} onClick={() => setIdUser(-1)}>
            <Add />
          </IconButton>
        </Typography>

        <DataGrid
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
          rows={users}
          columns={columns}
          getRowId={row => row.idUser}
          onRowClick={params => setIdUser(Number(params.id))}
        />
      </Box>
    </>
  );
};
